/**
 * ProductListCard Component - Unified List Layout
 *
 * Horizontal card mirroring Home/Shop grid hierarchy:
 *   image → title → meta(location · time) → price → trust badge
 * Same green price token (.plc-price) as the grid card.
 */

import React, { memo } from 'react';
import { Heart, MapPin, Clock, Package, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatRelativePostedTime } from '../../utils/dataConsistency';
import WasslaImage from '../common/WasslaImage';
import './ProductListCard.css';

const BADGE_I18N = {
  trusted_seller: 'selltrus.trustedSeller',
  verified_seller: 'selltrus.verifiedSeller',
  active_seller: 'selltrus.activeSeller',
};

const ProductListCard = memo(function ProductListCard({
  product,
  imageUrl,
  title,
  price,
  location,
  isFavorite,
  onOpen,
  onToggleFavorite,
  onImageError,
  onImageLoad,
  testId,
  children,
}) {
  const { i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith('ar');
  const postedAgo = formatRelativePostedTime(product?.created_at, isRTL ? 'ar' : 'en');

  return (
    <article
      className="product-list-card"
      data-testid={testId}
      onClick={() => onOpen?.(product)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onOpen?.(product)}
    >
      {/* Image */}
      <div className="plc-image-wrap">
        {imageUrl ? (
          <WasslaImage
            src={imageUrl}
            alt={title || 'Product'}
            fallbackType="ad"
            testid={`wassla-image-list-${product?.ad_id || 'unknown'}`}
            onError={onImageError}
            onLoad={onImageLoad}
            showCaption={false}
          />
        ) : (
          <div className="plc-placeholder">
            <Package size={24} />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="plc-content">
        {/* iter288 — Service badge: small pill rendered above title
            when listing_type=='service'. Operator-spec requirement so
            buyers can tell a service from a product at a glance. */}
        {product?.listing_type === 'service' && (
          <span
            className="plc-service-badge"
            data-testid={`service-badge-${product?.ad_id}`}
            style={{
              display: 'inline-block',
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              padding: '2px 8px',
              borderRadius: 999,
              background: 'rgba(37,99,235,0.10)',
              color: '#2563eb',
              border: '1px solid rgba(37,99,235,0.25)',
              marginBottom: 4,
              alignSelf: 'flex-start',
            }}
          >
            {/* Bilingual via short label — full word would crowd the card. */}
            Service
          </span>
        )}

        {/* Title — primary identifier (matches Home/Shop grid order) */}
        <h3 className="plc-title">{title || 'Untitled'}</h3>

        {/* Meta row: location + time */}
        <div className="plc-meta">
          {location && (
            <span className="plc-location">
              <MapPin size={12} />
              <span>{location}</span>
            </span>
          )}
          {postedAgo && (
            <span className="plc-time">
              <Clock size={12} />
              <span>{postedAgo}</span>
            </span>
          )}
        </div>

        {/* Price — same green token as grid (.plc-price) */}
        <p className="plc-price">{price || 'Price N/A'}</p>

        {/* Seller badge from backend trust system */}
        <SellerBadgeTag badge={product?.seller?.badge} />

        {/* Children (for badges etc) */}
        {children}
      </div>

      {/* Favorite button */}
      {onToggleFavorite && (
        <button
          className={`plc-favorite ${isFavorite ? 'active' : ''}`}
          type="button"
          aria-label="Save"
          data-testid={`favorite-${product?.ad_id}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite?.(product?.ad_id, e);
          }}
        >
          <Heart size={18} fill={isFavorite ? 'currentColor' : 'none'} />
        </button>
      )}
    </article>
  );
});

export default ProductListCard;

function SellerBadgeTag({ badge }) {
  const { t } = useTranslation();
  const key = BADGE_I18N[badge];
  if (!key) return null;
  const variant = badge === 'trusted_seller' ? 'trusted' : badge === 'verified_seller' ? 'verified' : 'active';
  return (
    <span className={`plc-seller-badge plc-seller-badge--${variant}`} data-testid="seller-badge">
      <ShieldCheck size={10} />
      {t(key)}
    </span>
  );
}
